import { EncField, Encoding } from '@mode-vegas/vegas';
import * as $ from 'jquery';
import { capitalize, truncate } from 'lodash';

import { DataType, DateTime } from '@mode-switch/express';
import {
  ChartEncoding,
  ChartHierarchy,
  EncodingHelpers,
  RenderDataModel,
  GRAND_TOTALS_LABEL,
  TOTALS_TOKEN,
} from '@mode/charts/util-vegas';
import { ChartTypes, InterActions } from '@mode/shared/contract-common';
import { HashKey, getFieldIcon } from '@mode/shared/util-js';
import { CompiledAttr, Switch } from '@mode/shared/util-switch';
import * as MuzeChartDrilldown from './muze-chart-drilldown';

export interface Options {
  readonly enableExploration: boolean;
  readonly preventExpandingInteraction: boolean;
  readonly enableDynamicDrilldown: boolean;
  readonly enableTemporalDrill: boolean; // Temporary until the dynamic-drilldown feature flag is removed
  analyticsProps?: any;
  boundOnEmbedAction?: (args: { $action: any }) => void;
  generateUUID?: () => string;
  chartToken?: string;
  reportContext?: boolean;
  getIcon: (iconName: string) => string;
}

export function options(overrides: Partial<Options>): Options {
  return {
    enableExploration: false,
    preventExpandingInteraction: true,
    enableDynamicDrilldown: false,
    enableTemporalDrill: true,
    getIcon: (iconName: string) => iconName,
    ...overrides,
  };
}

/** The css class for the overall context menu */
const topLevelContextMenuClass = 'chart-context-menu';
/** The css class for the menu item which launches the cascading drilldown field menu */
const drillIntoMenuItemClass = 'drill-into-menu-item';
/** The css class for the entire content of the cascading drill menu (including the search field) */
const fieldListMenuContentClass = 'field-list-menu-content';
/** The css class for the container holding the list of fields */
const fieldListClass = 'field-list';
/** The css class for a field in the field list menu */
const fieldListMenuItemClass = 'field-list-menu-item';
/** The css class for the label of the field in the field list menu */
const fieldLabelClass = 'field-label';

/** Expected heights of various menu items */
const MENU_HEADER_HEIGHT = 36;
const SEARCH_INPUT_HEIGHT = 32;
const CONTEXT_MENU_ITEM_HEIGHT = 24;
const DIVIDER_HEIGHT = 9;
const FIELD_LIST_PADDING_TOP = 4;
const FIELD_LIST_PADDING_BOTTOM = 4;
/** Maximum percent of total window height that cascading menu can use */
const FIELD_LIST_HEIGHT_FACTOR = 0.8;
/** Space between the bottom of the context menu and the bottom of the window */
const MENU_BOTTOM_PADDING = 8;

/** DOM Element attributes */
const IS_OPEN_ATTR = 'is-open';
const DATE_ID_ATTR = 'date-id';
const PARENT_ID_ATTR = 'parent-date-id';

let clickHandler: ((event: MouseEvent) => void) | null;
let scrollHandler: ((event: Event) => void) | null;

function appendMenu(menu: JQuery): void {
  $(document.body).append(menu);
}

function removeMenu(): void {
  const menu = $(document.body).find(`.${topLevelContextMenuClass}`);
  menu.remove();
}

function enableTooltips(chart: any): void {
  if (typeof chart.firebolt === 'function') {
    try {
      chart.firebolt().registerPhysicalBehaviouralMap({
        hover: {
          behaviours: ['highlight'],
        },
      });
    } catch (e) {
      console.error('muze tooltip enabled failure', e);
    }
  }
}

function disableTooltips(chart: any): void {
  if (typeof chart.firebolt === 'function') {
    try {
      // Clear any tooltips that might be showing
      chart.firebolt().dispatchBehaviour('highlight', { criterial: null });
      // Turn off tooltips while showing the menu
      chart.firebolt().dissociateBehaviour(['highlight', 'hover']);
    } catch (e) {
      console.error('muze tooltip disabled failure', e);
    }
  }
}

function addClickListener(chart: any): void {
  if (scrollHandler != null) {
    removeClickListener();
  }

  clickHandler = (event) => {
    if (event.target && $(event.target).closest(`.${topLevelContextMenuClass}`).length === 0) {
      clear(chart);
    }
  };

  document.addEventListener('click', clickHandler, true);
}

function removeClickListener(): void {
  if (clickHandler) {
    document.removeEventListener('click', clickHandler, true);
  }
  clickHandler = null;
}

function addScrollListener(chart: any): void {
  if (scrollHandler != null) {
    removeScrollListener();
  }

  scrollHandler = (event) => {
    // If we are not scrolling the field list, then clear the menu.
    if (event.target && !$(event.target).hasClass(`${fieldListClass}`)) {
      clear(chart);
    }
  };
  document.addEventListener('scroll', scrollHandler, true);
}

function removeScrollListener(): void {
  if (scrollHandler) {
    document.removeEventListener('scroll', scrollHandler, true);
  }
  scrollHandler = null;
}

function hasExploreMenuItem(opts: Options) {
  return opts.enableExploration;
}

function hasViewMenuItem(opts: Options) {
  return !opts.preventExpandingInteraction;
}

function hasDrilldownMenuItem(model: RenderDataModel, encoding: Encoding) {
  const drillLevels = ChartHierarchy.getDrillLevelsForEncoding(model, encoding);
  return hasDrillableLevel(drillLevels);
}

function hasDrillableLevel(levels: Array<ChartHierarchy.DrillLevel>) {
  return levels.some((level) => level.currentDatePart != level.nextDatePart);
}

export function clear(chart: any): void {
  removeClickListener();
  removeScrollListener();
  enableTooltips(chart);
  removeMenu();
}

export function hasItems(model: RenderDataModel, encoding: Encoding, opts: Options): boolean {
  const hasExploreItem = hasExploreMenuItem(opts);

  const hasViewItem = hasViewMenuItem(opts);

  const hasDrilldownItem = hasDrilldownMenuItem(model, encoding);

  return hasExploreItem || hasViewItem || hasDrilldownItem;
}

export function show(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  chart: any,
  model: RenderDataModel,
  encoding: Encoding,
  opts: Options
): void {
  const menu = make(dispatch, action, model, encoding, opts);
  removeMenu();
  disableTooltips(chart);
  addClickListener(chart);
  addScrollListener(chart);
  appendMenu(menu);
}

function make(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  model: RenderDataModel,
  encoding: Encoding,
  opts: Options
): JQuery {
  let seriesName = action.target.series.name;
  // This is basically an attempt to detect nulls in various types of charts and render the correct context menu
  // that shows the user the view data and other options.
  if ((action.target.x && action.target.x.value === null) || action.target.series.color?.value === null) {
    seriesName = 'null';
  }
  const seriesColor = action.target.series.color?.color;

  let contextMenuEl = $(`<div class="${topLevelContextMenuClass}"></div>`);
  let menuHeight = 0;
  if (seriesColor) {
    contextMenuEl = contextMenuEl.append(makeHeader(seriesName, seriesColor));
    menuHeight += MENU_HEADER_HEIGHT;
  }

  if (hasExploreMenuItem(opts)) {
    const exploreMenuEl = makeExploreMarkItems(contextMenuEl, action, model, encoding, dispatch, opts);
    if (exploreMenuEl) {
      contextMenuEl = contextMenuEl.append(exploreMenuEl);
      menuHeight += CONTEXT_MENU_ITEM_HEIGHT;
    }
  }

  if (hasViewMenuItem(opts)) {
    contextMenuEl = contextMenuEl.append(makeViewDataItem(dispatch, action, opts));
    menuHeight += CONTEXT_MENU_ITEM_HEIGHT;
  }

  const dynamicDrillableFields = getDynamicDrillFields(encoding, action.target.series.id);
  const supportsDynamicDrilldown = opts.enableDynamicDrilldown && dynamicDrillableFields.length > 0;

  // When dynamic drill down is enabled and the feature flag is removed, we can remove
  // temporal drilling completely.
  const temporalDrillLevels = ChartHierarchy.getDrillLevelsForEncoding(model, encoding, action.target.series.id);
  const hasDrillableTemporalLevels = hasDrillableLevel(temporalDrillLevels);
  // Add temporal drill if:
  //  - temporal drill is enabled and there is something to drill on
  //  - dynamic drill is enabled, but there are no dynamic drill fields and there are temporal drill fields.
  // This lets us fall back to some of the specific drill cases like drilling on the color in a pie chart.
  const isDrillable =
    (opts.enableTemporalDrill && hasDrillableTemporalLevels) ||
    (!opts.enableTemporalDrill && dynamicDrillableFields.length === 0 && hasDrillableTemporalLevels);

  const addDivider =
    (opts.enableExploration || !opts.preventExpandingInteraction) && (isDrillable || supportsDynamicDrilldown);
  if (addDivider) {
    contextMenuEl = contextMenuEl.append(makeDivider());
    menuHeight += DIVIDER_HEIGHT;
  }

  if (isDrillable) {
    temporalDrillLevels.forEach((level) => {
      contextMenuEl = contextMenuEl.append(makeDrilldownItem(dispatch, action, model, level, opts));
      menuHeight += CONTEXT_MENU_ITEM_HEIGHT;
    });
  }

  let drilldownCascadeMenuPosition = menuHeight;
  menuHeight += dynamicDrillableFields.length * CONTEXT_MENU_ITEM_HEIGHT;

  // Determine the vertical placement of the context menu. Position the menu such that the header
  // portion is above the cursor and the first menu item is just below.
  // The menu should not extend off the bottom of the window. If it is too tall for the default placement,
  // then place it so that the bottom of the menu is at the bottom of the window.
  const top = Math.min(
    action.position.clientY - MENU_HEADER_HEIGHT - 1,
    window.innerHeight - menuHeight - MENU_BOTTOM_PADDING
  );
  // Place the menu to the right of the cursor by default. If there isn't enough space in the window, shift
  // such that it fits in the window.
  const left = Math.min(action.position.clientX, window.innerWidth - 250);

  if (supportsDynamicDrilldown) {
    const calcCascadeMenuTopPosition = ($menu: JQuery) => {
      const cascadeMenuHeight = calculateFieldListMenuHeight($menu);
      // Set the cascade menu position such that the first field lines up with the drill down menu item
      // which is launching the cascade menu. This puts the search input field just above the menu.
      // If the menu would extend off the window, then shift it up so that the bottom of the menu is at the
      // bottom of the window.
      return Math.min(
        drilldownCascadeMenuPosition - SEARCH_INPUT_HEIGHT - FIELD_LIST_PADDING_TOP,
        window.innerHeight - top - cascadeMenuHeight - MENU_BOTTOM_PADDING
      );
    };

    dynamicDrillableFields.forEach((axisField) => {
      const primaryDrillMenu = makeDynamicDrilldownItem(dispatch, action, model, axisField, opts);
      const fieldListMenuContent = primaryDrillMenu.children(`.${fieldListMenuContentClass}`);
      fieldListMenuContent.css('top', `${calcCascadeMenuTopPosition(primaryDrillMenu)}px`);
      // Show on the left or right depending on space
      const position = left + 500 < window.innerWidth ? 'left' : 'right';
      fieldListMenuContent.css(position, '100%');

      contextMenuEl.append(primaryDrillMenu);
      drilldownCascadeMenuPosition += CONTEXT_MENU_ITEM_HEIGHT;
    });
  }

  contextMenuEl.css({
    opacity: 1,
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
  });
  return contextMenuEl;
}

function makeDivider(): JQuery {
  return $(`<div class="section-divider"></div>`);
}

function makeHeader(seriesName: string, seriesColor: string): JQuery {
  let labelText = '';
  if (seriesName === null) {
    labelText = 'null';
  } else {
    labelText = seriesName.length > 25 ? truncate(seriesName, { length: 25 }) : seriesName;
  }
  return $(`
      <div class="section-descriptor">
        <div class="section-color" style="color:${seriesColor}">●</div>
        <div class="value label-section-name">${labelText}</div>
      </div>
    `);
}

function makeExploreMarkItems(
  contextMenuEl: JQuery,
  action: InterActions.MarkClick,
  model: RenderDataModel,
  encoding: Encoding,
  dispatch: (item: InterActions.ContextItem) => void,
  opts: Options
): JQuery | undefined {
  switch (ChartEncoding.shape(encoding)) {
    case ChartEncoding.Shape.Stacked:
      return makeExploreStackedMenuItems(contextMenuEl, action, model, encoding, dispatch, opts);
    case ChartEncoding.Shape.Grouped:
      return makeExploreGroupedMenuItems(contextMenuEl, action, model, encoding, dispatch);
    case ChartEncoding.Shape.Combo:
      return makeExploreComboMenuItems(contextMenuEl);
    case ChartEncoding.Shape.Radial:
      return makeExploreRadialMenuItems(contextMenuEl, action, model, encoding, dispatch);
  }
}

function makeExploreItemElem(value: string): JQuery {
  value = value.length > 19 ? truncate(value, { length: 19 }) : value;

  return $(`
      <div class="context-menu-link">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">
            Explore <div class="value link-section-name">${value}</div>...
          </div>
        </div>
      </div>
    `);
}

function makeExploreMarkAction(
  action: InterActions.MarkClick,
  ...filters: Array<ChartTypes.Filter>
): InterActions.ExploreMarkItem {
  const { target } = action;
  return { target, filters, type: InterActions.ContextItemType.ExploreMark };
}

function makeExploreStackedMenuItems(
  contextMenuEl: JQuery,
  action: InterActions.MarkClick,
  model: RenderDataModel,
  encoding: Encoding,
  dispatch: (item: InterActions.ContextItem) => void,
  opts: Options
): JQuery {
  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;

  const seriesValue =
    MuzeChartDrilldown.formatValue(model, props.color.values[0], action.target.series.color?.value) || '';

  const seriesMenuItem = makeExploreItemElem(seriesValue).one('click', (event) => {
    if (opts.reportContext) {
      dispatchAnalyticsEvent('ClickExploreFromHere', opts);
    }
    dispatch(
      makeExploreMarkAction(action, {
        name: props.color.values[0].formula,
        value: action.target.series.color ? action.target.series.color.value : null,
      })
    );
  });

  return contextMenuEl.append(seriesMenuItem);
}

function makeExploreGroupedMenuItems(
  contextMenuEl: JQuery,
  action: InterActions.MarkClick,
  model: RenderDataModel,
  encoding: Encoding,
  dispatch: (item: InterActions.ContextItem) => void
): JQuery {
  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;

  const seriesValue =
    MuzeChartDrilldown.formatValue(model, props.color.values[0], action.target.series.color?.value) || '';

  const seriesMenuItem = makeExploreItemElem(seriesValue).one('click', (event) =>
    dispatch(
      makeExploreMarkAction(action, {
        name: props.color.values[0].formula,
        value: action.target.series.color ? action.target.series.color.value : null,
      })
    )
  );

  return contextMenuEl.append(seriesMenuItem);
}

function makeExploreComboMenuItems(contextMenuEl: JQuery): JQuery {
  return contextMenuEl;
}

function makeExploreRadialMenuItems(
  contextMenuEl: JQuery,
  action: InterActions.MarkClick,
  model: RenderDataModel,
  encoding: Encoding,
  dispatch: (item: InterActions.ContextItem) => void
): JQuery {
  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;

  const seriesValue =
    MuzeChartDrilldown.formatValue(model, props.color.values[0], action.target.series.color?.value) || '';

  const seriesMenuItem = makeExploreItemElem(seriesValue).one('click', (event) =>
    dispatch(
      makeExploreMarkAction(action, {
        name: props.color.values[0].formula,
        value: action.target.series.color ? action.target.series.color.value : null,
      })
    )
  );

  return contextMenuEl.append(seriesMenuItem);
}

function makeDrilldownItem(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  model: RenderDataModel,
  level: ChartHierarchy.DrillLevel,
  opts: Options
): JQuery {
  // Get the correct value
  const value = findTargetValue(action.target, level.field.id ?? '');
  const label = MuzeChartDrilldown.formatValue(model, level.field, value);
  const labelText = label && label.length > 18 ? truncate(label, { length: 18 }) : label;
  const isLowestDatePart = level.currentDatePart === level.nextDatePart;
  const actionDisabled = isLowestDatePart ? 'disabled' : '';
  const timeScaleTextPart1 = isLowestDatePart ? '' : ' by ';
  const timeScaleTextPart2 = isLowestDatePart ? '' : level.nextDatePart;

  const actionTarget = {
    ...action.target,
    drillTarget: {
      targetFieldId: level.field.id,
    },
  };

  return $(`
      <div class="context-menu-link ${actionDisabled}">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">
            Drilldown <div class="value link-section-name">${labelText}</div>${timeScaleTextPart1}<div class="value time-scale">${timeScaleTextPart2}</div>
          </div>
        </div>
      </div>
    `)
    .one('click', (event) => {
      if (opts.reportContext) {
        dispatchAnalyticsEvent('ClickDrilldownInterval', opts);
      }
      dispatch({
        target: actionTarget,
        type: InterActions.ContextItemType.Drilldown,
      });
    })
    .on('mouseenter', (event) => {
      hideFieldListCascadingMenu($(event.currentTarget));
    });
}

function findTargetValue(target: ChartTypes.MarkTarget, id: string) {
  // For Muze charts, we put all of the target columns in 'columns'. See muze-chart-renderer.registerClickHandler
  const field = target.columns?.find((field) => field?.id === id);
  return field != null ? field.value : '';
}

function makeViewDataItem(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  opts: Options
): JQuery {
  let viewDataLabel = 'View Data...';

  return $(`
      <div class="context-menu-link">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">${viewDataLabel}</div>
        </div>
      </div>
    `)
    .one('click', (event) => {
      if (opts.reportContext) {
        dispatchAnalyticsEvent('ClickViewData', opts);
      }
      dispatch({
        target: action.target,
        type: InterActions.ContextItemType.ViewData,
      });
    })
    .on('mouseenter', (event) => {
      hideFieldListCascadingMenu($(event.currentTarget));
    });
}

function makeDynamicDrilldownItem(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  model: RenderDataModel,
  targetField: EncField,
  opts: Options
) {
  let fieldListSubMenu = $(`
    <div class="chart-context-menu ${fieldListMenuContentClass}"></div>
  `);

  // Add search field
  fieldListSubMenu.append(makeTypeaheadSearch(opts));

  const modeModel = (model as any).model;

  // Build the field list
  const fieldList = $(`<div class="${fieldListClass}"></div>`);
  fieldList.css('max-height', window.innerHeight * FIELD_LIST_HEIGHT_FACTOR - SEARCH_INPUT_HEIGHT);
  // Add all dimensions
  const dimensions = Switch.Attrs.dimensions(modeModel, modeModel.name)
    .map((attr) => CompiledAttr.fromAttr((model as any).view, attr))
    .filter((compiled) => compiled.isValid)
    .sort((a, b) => compareAttrs(a, b));

  dimensions.forEach((attr) => {
    fieldList.append(makeDynamicDrilldownSubItem(dispatch, action, targetField, attr, opts));
  });

  // Add all measures
  const measures = Switch.Attrs.measures(modeModel, modeModel.name)
    .map((attr) => CompiledAttr.fromAttr((model as any).view, attr))
    .filter((compiled) => compiled.isValid)
    .sort((a, b) => compareAttrs(a, b));

  measures.forEach((attr) => {
    fieldList.append(makeDynamicDrilldownSubItem(dispatch, action, targetField, attr, opts));
  });

  // Add the field list to the submenu
  fieldListSubMenu.append(fieldList);

  // Build the main menu item
  const drillValue = targetField.id ? findTargetValue(action.target, targetField.id) : undefined;
  const label =
    drillValue === TOTALS_TOKEN ? GRAND_TOTALS_LABEL : MuzeChartDrilldown.formatValue(model, targetField, drillValue);
  const drillIntoMenuLabel =
    label != null
      ? $(`<div class="drill-into-menu-label"></div>`)
          .append('Drill into ')
          .append($(`<span class="drill-into-value"></span>`).text(`${truncate(label, { length: 36 })}`))
          .append(' by ')
      : $(`<div class="drill-into-menu-label"> Drill down on </div>`);

  const iconData = opts.getIcon('icon-caret-right');

  return $(`
    <div class="${drillIntoMenuItemClass}"></div>
  `)
    .append(drillIntoMenuLabel)
    .append(`<div class="menu-open-icon">${iconData}</div>`)
    .append(fieldListSubMenu)
    .on('mouseenter', (event) => {
      // Show the field list menu
      $(event.currentTarget).children(`.${fieldListMenuContentClass}`).show();
      // Hide any other cascading menus
      hideFieldListCascadingMenu($(event.currentTarget));
    });
}

function makeTypeaheadSearch(opts: Options) {
  const clearIconData = opts.getIcon('icon-close');
  const clearButton = $(`<div class="search-icon" style="display: none">${clearIconData}</div>`).on(
    'click',
    (event) => {
      // Clear the search field
      $(event.currentTarget).siblings('#drill-down-search-input').val('').trigger('input');
    }
  );

  const searchIconData = opts.getIcon('icon-search');
  const searchIcon = $(`<div class="search-icon" style="cursor: default">${searchIconData}</div>`);

  return $(`
    <div class="field-list-search-box">
      <input id="drill-down-search-input" type="text" placeholder="Search fields..." ></input>
    </div>
  `)
    .append(clearButton)
    .append(searchIcon)
    .on('input', (event) => {
      const input = event.target as HTMLInputElement;
      if (input != null) {
        // Case insensitive search
        const searchValue = input.value.toLowerCase();

        // Show the close (X) icon if the search field is not empty;
        // otherwise, show the magnifying glass
        if (searchValue.length > 0) {
          searchIcon.hide();
          clearButton.show();
        } else {
          searchIcon.show();
          clearButton.hide();
        }

        // Show or hide the field list menu items based on whether the field name
        // contains the search value. There is an added complication with date fields
        // since those can be expanded to show the DateUnit.
        // If the date field matches the search term and it's open, then both the date
        // field and all of the DateUnit menu items should be displayed.
        // If the date field doesn't match the search term and it's open, then hide
        // the date field and all of the DateUnit menu items too.
        $(event.currentTarget)
          .siblings(`.${fieldListClass}`)
          .children(':not(.date-menu-text)') // don't search the DateUnit menu items
          .each((_, element) => {
            $(element)
              .children(`.${fieldLabelClass}`)
              .each((_, menuText) => {
                const fieldName = menuText.textContent?.toLowerCase();
                const dateIdAttr = $(element).attr(DATE_ID_ATTR);
                const isOpen = $(element).attr(IS_OPEN_ATTR) === 'true';
                if (fieldName?.includes(searchValue)) {
                  // Show the element since it contains the search value
                  $(element).show();
                  // If this menu item is open, then also show any DateUnit
                  // menu items that are related.
                  if (isOpen) {
                    $(element)
                      .siblings(`[${PARENT_ID_ATTR}='${dateIdAttr}']`)
                      .each((_, child) => {
                        $(child).show();
                      });
                  }
                } else {
                  // Hide the element
                  $(element).hide();
                  // Find any DateUnit menu items that are related to this menu item
                  // and hide those too.
                  $(element)
                    .siblings(`[${PARENT_ID_ATTR}='${dateIdAttr}']`)
                    .each((i, child) => {
                      $(child).hide();
                    });
                }
              });
          });
      }
    });
}

function makeDynamicDrilldownSubItem(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  targetField: EncField,
  attr: CompiledAttr,
  opts: Options
) {
  const isDateTime = attr.attrDataType === DataType.Name.DateTime || attr.attrDataType === DataType.Name.Timestamp;

  return isDateTime
    ? makeDateFieldMenuItems(dispatch, action, targetField, attr, opts)
    : makeFieldMenuItem(dispatch, action, targetField, attr, opts);
}

function makeFieldMenuItem(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  targetField: EncField,
  attr: CompiledAttr,
  opts: Options
) {
  const menuItem = $(`<div class="${fieldListMenuItemClass}"></div>`);

  const fieldIcon = getFieldIcon(attr.formulaDataType, attr.isCalc);
  const iconData = opts.getIcon(fieldIcon);
  const iconClass = attr.isDiscrete ? 'discrete' : 'continuous';
  const fieldIconItem = $(`<div class="field-type-icon ${iconClass}">${iconData}</div>`);

  menuItem.append(fieldIconItem);

  const fieldName = $(`<div class="${fieldLabelClass}"></div>`).text(`${truncate(attr.fieldName, { length: 28 })}`);

  menuItem.append(fieldName);
  menuItem.one('click', () => {
    // Clicking on a field menu item will trigger a Drilldown action.
    dispatch({
      target: action.target,
      type: InterActions.ContextItemType.DynamicDrillDown,
      targetFieldId: targetField.id ?? '',
      drillToField: attr.fieldName ?? '',
      dateTruncUnit: undefined,
    });
  });

  return menuItem;
}

function makeDateFieldMenuItems(
  dispatch: (item: InterActions.ContextItem) => void,
  action: InterActions.MarkClick,
  targetField: EncField,
  attr: CompiledAttr,
  opts: Options
) {
  // Create the main menu item which can be expanded to show the DateUnits
  const menuItem = $(`<div class="${fieldListMenuItemClass}"></div>`);
  const iconCaretMiniData = opts.getIcon('icon-chevron-right');
  const expandButton = $(`<div class="date-menu-expand-icon">${iconCaretMiniData}</div>`);
  menuItem.append(expandButton);
  menuItem.append(`<div class="${fieldLabelClass} expandable">${truncate(attr.fieldName, { length: 28 })}</div>`);

  // Since the main field menu items and the DateUnit menu items are all at the same level
  // in the DOM, we establish a link between them using an id hashed from the field formula.
  // This makes it easy to hide/show the DateUnit menu items when the user expands/collapses
  // a particular date field.
  const formulaHash = `D${HashKey.hashFn(attr.formula).toString(16)}`;
  menuItem.attr(DATE_ID_ATTR, formulaHash);

  // Create a menu item for each DateUnit. This are siblings of the main Date field menu item.
  // They will be shown/hidden based on the expand/collapse state of the Date field menu item.
  const dateMenuItems: JQuery[] = [];

  const dateFieldIcon = opts.getIcon('icon-field-date');
  // Build a menu item for each DateUnit.
  [
    DateTime.Unit.Year,
    DateTime.Unit.Quarter,
    DateTime.Unit.Month,
    DateTime.Unit.Week,
    DateTime.Unit.Day,
    DateTime.Unit.Hour,
    DateTime.Unit.Minute,
    DateTime.Unit.Second,
  ].forEach((dateUnit) => {
    const subMenuItem = $(
      `<div class="${fieldListMenuItemClass} date-menu-text">
            <div class="field-type-icon discrete">${dateFieldIcon}</div>
            <div class="${fieldLabelClass}">${capitalize(dateUnit)}</div>
        </div>`
    ).one('click', () => {
      // Clicking on a DateUnit menu item will immediately dispatch a Drilldown action
      // which includes the selected DateUnit.
      dispatch({
        target: action.target,
        type: InterActions.ContextItemType.DynamicDrillDown,
        targetFieldId: targetField.id ?? '',
        drillToField: attr.fieldName ?? '',
        dateTruncUnit: dateUnit,
      });
    });
    subMenuItem.attr(PARENT_ID_ATTR, formulaHash);
    subMenuItem.hide();
    dateMenuItems.push(subMenuItem);
  });

  menuItem.on('click', (event) => {
    // Clicking the main field menu item will hide/show the DateUnit menu items. Unlike
    // other field menu items, it does not trigger a Drilldown action.
    const target = $(event.currentTarget);
    const isOpen = target.attr(IS_OPEN_ATTR) === 'true';
    // The 'children' are sibling menu items with matching 'id' attributes.
    const children = target.siblings(`[${PARENT_ID_ATTR}='${formulaHash}']`);
    const fieldListMenu = target.closest(`.${fieldListMenuContentClass}`);
    const oldMenuHeight = calculateFieldListMenuHeight(fieldListMenu);
    if (isOpen) {
      children.hide();
      target.removeAttr(IS_OPEN_ATTR);
    } else {
      children.show();
      target.attr(IS_OPEN_ATTR, 'true');
    }

    // Expanding/collapsing a Date field menu item _may_ change the height of the cascading
    // menu (it depends on whether the menu is already the max height or not). This isn't an
    // issue unless the change in menu height would cause it to display offscreen.
    // If the menu height changes, we will reposition it to make sure that it is visible.
    const newMenuHeight = calculateFieldListMenuHeight(fieldListMenu);
    if (oldMenuHeight !== newMenuHeight) {
      // Get the absolute position of the top level context menu.
      const contextMenu = fieldListMenu.parents(`.${topLevelContextMenuClass}`);
      const contextMenuBoundingRect = contextMenu[0].getBoundingClientRect();

      // Get the absolute position of the menu item which is launching the
      // field context menu.
      const parentMenuItem = fieldListMenu.closest(`.${drillIntoMenuItemClass}`);
      const parentMenuItemBoundingRect = parentMenuItem[0].getBoundingClientRect();

      // Calculate the appropriate offset from the parent menu item which either places
      // the context menu directly next to the parent menu item, or places it such
      // that it fits completely in the window.
      const absoluteTopY = Math.min(
        parentMenuItemBoundingRect.y - SEARCH_INPUT_HEIGHT - FIELD_LIST_PADDING_TOP,
        window.innerHeight - newMenuHeight - MENU_BOTTOM_PADDING
      );

      fieldListMenu.css('top', absoluteTopY - contextMenuBoundingRect.y);
    }
  });

  return [menuItem, ...dateMenuItems];
}

function getDynamicDrillFields(encoding: Encoding, seriesId: string | undefined) {
  const { primary, secondary } = EncodingHelpers.extractPrimarySecondaryAxes(encoding);

  if (encoding.marks.channel === '*') {
    if (EncodingHelpers.isRadialEncoding(encoding)) {
      // Special handling for pie/donut. Always return the color
      return encoding.marks.properties.color.values;
    }
    return primary.concat(secondary);
  }

  if (secondary.length > 0) {
    return secondary;
  }
  // If there are no drillable fields, fall back to color, then detail, then size
  // from the specified series.
  const series = encoding.marks.series.find((s) => s.field === seriesId);
  if (series) {
    if (series.properties.color.values.length > 0) {
      return series.properties.color.values;
    }
    if (series.properties.detail.values.length > 0) {
      return series.properties.detail.values;
    }
    return series.properties.size.values;
  }
  return [];
}

function hideFieldListCascadingMenu($mainMenuItem: JQuery) {
  $mainMenuItem.siblings(`.${drillIntoMenuItemClass}`).children(`.${fieldListMenuContentClass}`).hide();
}

function calculateFieldListMenuHeight($menu: JQuery) {
  // The maximum height of the field list menu is based on the window height.
  const maxFieldListMenuHeight = window.innerHeight * FIELD_LIST_HEIGHT_FACTOR;
  return Math.min(
    maxFieldListMenuHeight,
    SEARCH_INPUT_HEIGHT +
      FIELD_LIST_PADDING_TOP +
      FIELD_LIST_PADDING_BOTTOM +
      $menu.find(`.${fieldListMenuItemClass}:not([style*="display: none"])`).length * CONTEXT_MENU_ITEM_HEIGHT
  );
}

function compareAttrs(a: CompiledAttr, b: CompiledAttr) {
  if (a.fieldName === undefined && b.fieldName === undefined) {
    return 0;
  }
  if (a.fieldName === undefined) {
    return -1;
  }
  if (b.fieldName === undefined) {
    return 1;
  }
  return a.fieldName.localeCompare(b.fieldName);
}

function dispatchAnalyticsEvent(eventType: any, opts: Options): void {
  const analyticsProps = opts.analyticsProps;
  analyticsProps.event_id = opts.generateUUID && opts.generateUUID(); // generate new event_id since the existing one is the same as the click_chart_data_series event
  opts.boundOnEmbedAction &&
    opts.boundOnEmbedAction({
      $action: {
        type: eventType,
        chartToken: opts.chartToken,
        props: analyticsProps,
      },
    });
}
